import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Upload, FileText, X } from 'lucide-react';
import { scanCvFileForSkills } from '../../../utils/cvSkillExtraction';
import CVSkillConfirmation from '../CVSkillConfirmation';

const CLEARANCE_LEVELS = ['None', 'Baseline', 'NV1', 'NV2', 'TSPV'];

const CITIZENSHIP_OPTIONS = [
  'Australian Citizen',
  'Australian Permanent Resident',
  'New Zealand Citizen',
  'Visa Holder – Full Work Rights',
  'Visa Holder – Restricted Work Rights',
  'Other / Prefer not to say',
];

const EMPLOYMENT_TYPE_OPTIONS = [
  'Permanent – Full-time',
  'Permanent – Part-time',
  'Fixed-term Contract',
  'Contractor – Full-time',
  'Contractor – Part-time',
  'Casual',
  'Internship / Graduate',
  'Temporary / Agency Contract',
];

// Types that require payroll preference selection
const CONTRACTOR_TYPES = ['Contractor – Full-time', 'Contractor – Part-time', 'Casual'];

const StepProfessionalInfo = ({ formData, setFormData }) => {
  const [dragActive, setDragActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [excludedCount, setExcludedCount] = useState(0);
  // Tracks which of formData.skills came from the CV, so the confirmation UI only
  // manages those and never touches manually added skills
  const [cvSkillIds, setCvSkillIds] = useState(new Set());

  const update = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleEmploymentType = (type) => {
    const current = formData.employment_types || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];

    // Clear payroll fields if no contractor types remain selected
    const hasContractorType = updated.some(t => CONTRACTOR_TYPES.includes(t));
    if (!hasContractorType) {
      setFormData(prev => ({
        ...prev,
        employment_types: updated,
        payroll_type: '',
        pty_ltd_abn: '',
      }));
    } else {
      update('employment_types', updated);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF or Word document.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File must be under 10MB.');
      return;
    }
    update('cv_file', file);
    scanForSkills(file);
  };

  // Read skills out of the CV so the candidate can confirm them. Skills already on
  // the profile are filtered out server-side, so someone who applied publicly before
  // signing up is not asked about the same skills again.
  const scanForSkills = async (file) => {
    setScanning(true);
    setScanError(null);
    setExcludedCount(0);

    try {
      const { matched, excludedCount: excluded } = await scanCvFileForSkills(file, {
        context: 'onboarding',
      });
      setExcludedCount(excluded);

      const alreadyPresent = new Set((formData.skills || []).map(s => s.id));
      const newOnes = matched
        .filter(m => !alreadyPresent.has(m.skill_id))
        .map(m => ({
          id: m.skill_id,
          name: m.name,
          category: m.category,
          level: null,
          fromCv: true,
        }));

      setCvSkillIds(new Set(newOnes.map(s => s.id)));
      if (newOnes.length > 0) {
        update('skills', [...(formData.skills || []), ...newOnes]);
      }
    } catch (err) {
      // Never block onboarding on this
      console.error('CV skill scan failed:', err);
      setScanError(err.message || 'Could not read skills from your CV');
    } finally {
      setScanning(false);
    }
  };

  // Remove/clear handlers operate on the CV-derived subset only, leaving any skills
  // the candidate added by hand untouched.
  const cvDerivedSkills = (formData.skills || [])
    .filter(s => cvSkillIds.has(s.id))
    .map(s => ({ skill_id: s.id, name: s.name, category: s.category }));

  const handleCvSkillsChange = (kept) => {
    const keptIds = new Set(kept.map(s => s.skill_id));
    setCvSkillIds(keptIds);
    update(
      'skills',
      (formData.skills || []).filter(s => !s.fromCv || keptIds.has(s.id))
    );
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Check if payroll preference should be shown
  const showPayrollPreference = (formData.employment_types || []).some(t => CONTRACTOR_TYPES.includes(t));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Professional Details</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Help us match you with the right opportunities. All fields are optional.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
        <div className="space-y-2">
          <Label htmlFor="citizenship_status">Citizenship / Residency Status</Label>
          <select
            id="citizenship_status"
            value={formData.citizenship_status || ''}
            onChange={(e) => update('citizenship_status', e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select status</option>
            {CITIZENSHIP_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="clearance_level">Security Clearance</Label>
          <select
            id="clearance_level"
            value={formData.clearance_level || ''}
            onChange={(e) => update('clearance_level', e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select clearance level</option>
            {CLEARANCE_LEVELS.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notice_period">Notice Period</Label>
        <Input
          id="notice_period"
          value={formData.notice_period || ''}
          onChange={(e) => update('notice_period', e.target.value)}
          placeholder="e.g. 2 weeks, Immediately available"
        />
      </div>

      {/* Employment Type Multi-select */}
      <div className="space-y-2">
        <Label>Employment Type</Label>
        <p className="text-xs text-muted-foreground">Select all that apply</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {EMPLOYMENT_TYPE_OPTIONS.map(type => {
            const isSelected = (formData.employment_types || []).includes(type);
            return (
              <label
                key={type}
                className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                  isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleEmploymentType(type)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm">{type}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Payroll Preference - only shown for contractor/casual types */}
      {showPayrollPreference && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="space-y-2">
            <Label htmlFor="payroll_type">Payroll Preference</Label>
            <select
              id="payroll_type"
              value={formData.payroll_type || ''}
              onChange={(e) => update('payroll_type', e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select payroll preference</option>
              <option value="PAYG">PAYG</option>
              <option value="PTY LTD">PTY LTD</option>
            </select>
          </div>
          {formData.payroll_type === 'PTY LTD' && (
            <div className="space-y-2">
              <Label htmlFor="pty_ltd_abn">ABN</Label>
              <Input
                id="pty_ltd_abn"
                value={formData.pty_ltd_abn || ''}
                onChange={(e) => update('pty_ltd_abn', e.target.value)}
                placeholder="e.g. 12 345 678 901"
              />
            </div>
          )}
        </div>
      )}

      {/* Resume Upload */}
      <div className="space-y-2">
        <Label>Resume</Label>
        {formData.cv_file ? (
          <div className="flex items-center gap-3 p-3 border rounded-md bg-muted/50">
            <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{formData.cv_file.name}</p>
              <p className="text-xs text-muted-foreground">
                {(formData.cv_file.size / 1024).toFixed(0)} KB
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => update('cv_file', null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div
            className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
              dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('cv-upload-input').click()}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">Drop your resume here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">PDF or Word, max 10MB</p>
            <input
              id="cv-upload-input"
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
            />
          </div>
        )}

        {(scanning || scanError || cvDerivedSkills.length > 0 || excludedCount > 0) && (
          <CVSkillConfirmation
            variant="inline"
            skills={cvDerivedSkills}
            loading={scanning}
            error={scanError}
            excludedCount={excludedCount}
            onChange={handleCvSkillsChange}
          />
        )}
      </div>
    </div>
  );
};

export default StepProfessionalInfo;
